import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import type { DeliveryTableRow } from 'ui/organisms/DeliveryTable/DeliveryTable.types';

type Translate = (key: string) => string;

export const getDeliveryRows = (t: Translate): DeliveryTableRow[] => [
  {
    name: t('Platform'),
    tag: t('NO INTEGRATION'),
    desc: t(
      'Search a company, open its filed accounts and export the history. Built for analysts working one file at a time.',
    ),
    actions: [{ label: t('Start free trial'), href: '/free-trial' }],
  },
  {
    name: 'API',
    tag: 'REST · JSON',
    desc: t(
      'Deterministic endpoints for financials, officers, ownership and credit, plus agentic Regis queries. Both run on one key, priced per result rather than per token.',
    ),
    actions: [{ label: t('Read the docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk data'),
    tag: 'CSV · S3 · SFTP',
    desc: t(
      'Full financial history by file, loaded into your own warehouse or model. Reseller and derivative rights available.',
    ),
    actions: [{ label: t('Talk to sales'), href: '/book-a-demo' }],
  },
  {
    name: 'CRM',
    tag: 'Salesforce · HubSpot · Dynamics',
    desc: t(
      'Filed financials written onto the record your team already works in, so the accounts sit next to the account rather than in another tab.',
    ),
    actions: [
      { label: t('Add to Salesforce'), href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
      { label: t('Add to HubSpot'), href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
    ],
  },
  {
    name: t('Browser extension'),
    tag: 'CHROME',
    desc: t("Pull a company's filed numbers from any website or LinkedIn profile without leaving the page."),
    actions: [{ label: t('Add to Chrome'), href: 'https://chromewebstore.google.com/detail/global-database-extension/bjahjijchjnokbgbahdmfcjoblkicank' }],
  },
  {
    name: 'Claude & ChatGPT',
    tag: t('CONNECTOR'),
    desc: t(
      "One-click connectors for both assistants. Ask for a company's accounts in plain language and get the filed figures back.",
    ),
    actions: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: 'MCP server',
    tag: t('ENDPOINT'),
    desc: t(
      'Our MCP server exposes the platform as tools to Claude and any other Model Context Protocol client, on the same key and the same provenance fields.',
    ),
    actions: [{ label: t('View setup'), href: '/mcp-integration' }],
  },
  {
    name: 'Regis AI',
    tag: t('In platform · API · Agentic'),
    desc: t(
      'Our own AI model, built into the platform interface and available over the API. Ask a question that spans filings, entities or borders and it plans which registers can answer, calls them, and hands back every value with its register, reference and timestamp.',
    ),
    actions: [{ label: t('See Regis'), href: '/regis' }],
  },
];

export const getFaqItems = (t: Translate): FaqType[] => [
  {
    id: 'where-do-the-financials-come-from',
    default_opened: true,
    question: t('Where do the financials come from?'),
    answer: t(
      'From the accounts the company filed at its own register. We hold direct connections to more than 400 government registers and collect the filings ourselves, so you are buying from the source rather than from someone who bought it from the source.',
    ),
  },
  {
    id: 'what-if-a-company-has-not-filed',
    question: t('What if a company has not filed?'),
    answer: t(
      'The field is shown as missing and the register that is silent is named. A gap stays a gap, because an unfiled or overdue account is itself a signal and you should be able to see it.',
    ),
  },
  {
    id: 'how-do-you-get-figures-out-of-a-scanned-filing',
    question: t('How do you get figures out of a scanned filing?'),
    answer: t(
      'Where a register publishes XBRL we take the tagged data directly. Where it does not, the document is retrieved, read page by page with OCR, and the line items are extracted by models trained on filing formats. Either way the value keeps a reference to the filing it came from, so the figure and the evidence never come apart.',
    ),
  },
  {
    id: 'how-far-back-does-the-history-go',
    question: t('How far back does the history go?'),
    answer: t(
      "Up to ten years of filings where the register publishes them, so you can compare the same line across years on the company's own basis rather than on a standardised one.",
    ),
  },
  {
    id: 'which-countries-publish-full-accounts',
    question: t('Which countries publish full accounts?'),
    answer: t(
      'It varies, and the page will tell you which. Some registers publish full statutory accounts, some publish abridged or micro-entity accounts, and some publish very little. Coverage per country is set out in the data dictionary rather than averaged into a single claim.',
    ),
  },
  {
    id: 'how-is-this-different-from-dun-bradstreet-moodys-or-bureau-van-dijk',
    question: t("How is this different from Dun & Bradstreet, Moody's or Bureau van Dijk?"),
    answer: t(
      'Their strength is a standardised record built for comparability across a portfolio. Ours is depth and proximity to the document: the filing itself, the history around it, and the group it sits in. Many teams use both, for different jobs.',
    ),
  },
  {
    id: 'do-you-provide-anything-for-companies-that-file-almost-nothing',
    question: t('Do you provide anything for companies that file almost nothing?'),
    answer: t(
      'Yes. Micro-entities and exempt companies often file no profit and loss at all, so we publish a modelled revenue estimate alongside whatever was filed. It is labelled as an estimate everywhere it appears and is never mixed into the filed figures.',
    ),
  },
  {
    id: 'can-we-redistribute-the-financials-in-our-own-product',
    question: t('Can we redistribute the financials in our own product?'),
    answer: t(
      'Yes, under licence. Internal use, customer-facing display and redistribution are licensed and priced separately, and identifiers stay open so what you build is not locked to us.',
    ),
  },
  {
    id: 'how-do-we-get-it-into-our-models',
    question: t('How do we get it into our models?'),
    answer: t(
      'Bulk files into your warehouse, REST endpoints for on-demand pulls, or the MCP server and Regis for question-led work. The same figures and the same filing references reach you either way.',
    ),
  },
  {
    id: 'how-is-it-priced',
    question: t('How is it priced?'),
    answer: t(
      'Per result rather than per token, with volume and commitment agreed up front. Redistribution rights are priced as part of the licence rather than added afterwards.',
    ),
  },
];
